import { useState } from "react";
import { Archive, ArchiveRestore, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const OrganizerBulkBar = ({ selectedCount, total, allMatching, restoreMode, onSelectAllMatching, onClear, onApply }) => {
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  if (!selectedCount) return null;
  const verb = restoreMode ? "Restore" : "Archive";
  const noun = `${selectedCount} assignment${selectedCount === 1 ? "" : "s"}`;

  const apply = async () => {
    setWorking(true);
    try { await onApply(); setConfirming(false); } finally { setWorking(false); }
  };

  return (
    <div className="sticky bottom-20 sm:bottom-4 z-20 p-3 rounded-2xl bg-slate-900 text-white shadow-lg flex flex-wrap items-center gap-3" data-testid="organizer-bulk-bar">
      <div className="text-sm font-semibold" data-testid="organizer-selected-count">{noun} selected{allMatching ? " (all matching results)" : ""}</div>
      {!allMatching && total > selectedCount && (
        <button type="button" data-testid="organizer-select-all-matching" onClick={onSelectAllMatching} className="text-xs font-semibold text-teal-200 underline underline-offset-2">Select all {total} matching results</button>
      )}
      <div className="ml-auto flex items-center gap-2">
        <button type="button" data-testid="organizer-bulk-apply" onClick={() => setConfirming(true)}
          className={`inline-flex items-center gap-1.5 min-h-[40px] px-4 py-2 rounded-full text-sm font-semibold ${restoreMode ? "bg-teal-500 hover:bg-teal-400 text-slate-900" : "bg-amber-400 hover:bg-amber-300 text-slate-900"}`}>
          {restoreMode ? <ArchiveRestore size={14} /> : <Archive size={14} />} {verb} {selectedCount}
        </button>
        <button type="button" data-testid="organizer-bulk-clear" onClick={onClear} aria-label="Clear selection" className="grid place-items-center w-10 h-10 rounded-full hover:bg-white/10"><X size={16} /></button>
      </div>

      <AlertDialog open={confirming} onOpenChange={(open) => !working && setConfirming(open)}>
        <AlertDialogContent data-testid="organizer-bulk-dialog" className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{verb} {noun}?</AlertDialogTitle>
            <AlertDialogDescription data-testid="organizer-bulk-summary">
              {restoreMode
                ? `${noun} will move back into your everyday views.`
                : `${noun} will be archived. All student work, files, grades, feedback, and due dates stay saved, grades still count in the gradebook and reports, the student keeps seeing the work, and you can restore any of them from the Archived view.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="organizer-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="organizer-bulk-confirm" onClick={(event) => { event.preventDefault(); apply(); }} disabled={working} className={restoreMode ? "bg-teal-700 hover:bg-teal-800" : "bg-amber-600 hover:bg-amber-700"}>
              {working ? "Working…" : `${verb} ${noun}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
